import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useAppState, useCurrencyFormatting } from "@nosto/preact"
import { useState, useEffect, useMemo, useRef } from "preact/hooks"
import { closeSearchOverlay } from "../helpers"

import SubmitButton from "./elements/SubmitButton"
import AutocompleteProduct from "./AutocompleteProduct"

export default function Autocomplete() {
    const { products, keywords } = useResponse();
    const { query } = useAppState();
    const { format } = useCurrencyFormatting();
    const autocompleteRef = useRef(null);

    useEffect(() => {
        if(!autocompleteRef.current) return;

        const handleClick = e => e.target.closest('.ns-autocomplete-element') && closeSearchOverlay();

        autocompleteRef.current.addEventListener('click', handleClick);
        return () => autocompleteRef.current.removeEventListener('click', handleClick);
    }, [ autocompleteRef.current ])

    // dynamic colors 
    const [colors, setColors] = useState([]);

    const uniqueColors = useMemo(() => {
        if (!products.hits || !Array.isArray(products.hits)) [];

        const allColors = products?.hits
            .flatMap(hit => hit?.customFields || []) 
            .filter(field => field?.key === 'custom-product_family') 
            .map(field => field?.value)
            .filter(value => value !== null && value !== undefined && value !== '')
            .filter((family, index, array) => array.indexOf(family) === index);

        return allColors.sort();
    }, [products]);

    useEffect(() => {
        setColors(uniqueColors);
    }, [uniqueColors]);

    // dynamic sizes 
    const [sizes, setSizes] = useState([]);

    const uniqueSizes = useMemo(() => {
        if (!products.hits || !Array.isArray(products.hits)) [];
        
        const allSizes = products?.hits
            .flatMap(hit => hit?.skus || [])
            .filter(sku => sku?.availability === "InStock")
            .map(sku => sku?.customFields?.find(field => field?.key === 'size')?.value)
            .filter(size => size !== null && size !== undefined && size !== '')
            .filter((size, index, array) => array.indexOf(size) === index);

        return allSizes
    }, [products]);

    useEffect(() => {
        setSizes(uniqueSizes);
    }, [uniqueSizes]);

    if (!products?.hits?.length && !keywords?.hits?.length) return

    return (
        <div
            class="ns-autocomplete ns-fixed ns-d-flex ns-flex-column ns-flex-sm-row ns-align-items-start ns-background-white ns-border-box ns-mx-auto"
            data-nosto-element="autocomplete"
            id="nosto-autocomplete"
            ref={autocompleteRef}
        >
            <span 
                class="close-autocomplete"
                onClick={() => {
                    document.querySelector('.ns-overlay').click();
                    document.querySelector('.ns-overlay').style.display = "none"
                }}   
            >
                x
            </span>
            <div class="ns-w-100 ns-mt-sm-auto">
                <div class="ns-d-flex ns-flex-column ns-flex-sm-row" id="nosto-autocomplete-box">                    
                        <div
                            class={`ns-autocomplete-keywords ns-d-flex ns-flex-column ns-col-12 ns-col-sm-2`}
                        >
                            {/* <div class="ns-color-black ns-py-2 ns-px-4 ns-font-medium ns-font-4">
                                Keywords
                            </div> */}
                            <div class={`ns-autocomplete-header ns-font-bold ${keywords?.hits?.length > 0 ? "ns-d-block" : "ns-d-none"}`}>featured</div>
                            <div class="ns-d-flex ns-flex-column">
                                <Keywords
                                    hits={keywords.hits}
                                    keywordComponent={Keyword}
                                    elementComponent={Element}
                                    keywordInCategoryComponent={KeywordInCategory}
                                />
                            </div>
                            {!products?.hits?.length && <SubmitButton text="See all search results" />}
                        </div>
                    
                    <div class="ns-autocomplete-categories ns-d-flex ns-flex-column ns-col-12 ns-col-sm-2">
                        <div class="ns-autocomplete-header ns-font-bold">Color</div>
                        <div class="ns-d-flex ns-flex-column facets__list--color ns-top-colors ns-w-100 ns-mb-4" style="position: unset;">
                            {colors?.length > 0 && colors.map((color)=>{
                                let formattedColor;
                                let parts = color.split(" ");
                                if(parts?.length > 1){
                                    formattedColor = parts?.join("+");
                                } else {
                                    formattedColor = color;
                                }
                                const isBlackOrWhite = color === "Black" || color === "White";
                                const sanitizedValue = color.toLowerCase().replaceAll(" ", "-");
                                const hexColor = color === "Black" ? "#000000" : "#FFFFFF";
                                const labelStyle = isBlackOrWhite
                                    ? `background-color: ${hexColor};`
                                    : `background-size: cover; background-image: url(https://cdn.shopify.com/s/files/1/0158/2548/files/${sanitizedValue}.jpg)`;
                                return (
                                    <AutocompleteElement
                                        key={color}
                                        class="ns-clickable ns-py-2 ns-px-4 ns-text-no-underline"
                                        query={{ query: query.query }}
                                        filter={{
                                            field: 'customFields.custom-product_family',
                                            value: [color]
                                        }}
                                    >
                                        <li class="list-menu__item facets__item" data-value={sanitizedValue} style={{display: "inline-flex"}}>
                                            <input type="checkbox" />
                                            <label class="facet-checkbox" style={labelStyle}>
                                                <span class="visually-hidden"></span>
                                            </label>
                                            {color} 
                                        </li>
                                    </AutocompleteElement>
                                )
                            })}
                        </div>
                          <div class="ns-autocomplete-header ns-font-bold">Category</div>
                          <div class="ns-d-flex ns-flex-column ns-pop-suggestions ns-top-categories ns-mb-4">
                              {window.nostoSearch?.topCollections?.length > 0 && (
                                    <>{
                                        window.nostoSearch.topCollections.map((collection) => (
                                            <a 
                                                key={collection?.text}
                                                href={collection?.url} 
                                                class="ns-autocomplete-element ns-clickable ns-py-2 ns-px-4 ns-text-no-underline"
                                            >
                                                <span>{collection?.text}</span>
                                            </a>
                                        ))
                                    }</>
                              )}
                          </div>
                          <div class="ns-autocomplete-header ns-font-bold">Size</div>
                          <div class="ns-d-flex ns-flex-column ns-pop-suggestions ns-top-categories ns-mb-4">
                              {sizes?.length > 0 && sizes.map((size) => (
                                    <AutocompleteElement
                                        key={size}
                                        class="ns-clickable ns-py-2 ns-px-4 ns-text-no-underline"
                                        query={{ query: query.query }}
                                        filter={{
                                            field: "skus.customFields.size",
                                            value: [size]
                                        }}
                                    >
                                        <li class="list-menu__item facets__item" style={{display: "inline-flex"}}>
                                            {size} 
                                        </li>
                                    </AutocompleteElement>
                                )
                              )}
                          </div>
                             
                    </div>
                    {products?.hits?.length > 0 && (
                        <div class="ns-autocomplete-products ns-d-flex ns-flex-column ns-flex-grow-1">
                            {/* <div class="ns-color-black ns-py-2 ns-px-4 ns-font-medium ns-font-4">
                                Products
                            </div> */}
                            <div class="ns-autocomplete-header ns-font-bold">recommended for you</div>
                            <div class="nosto-products-container">
                                {products?.hits?.map(hit => (
                                    <AutocompleteProduct 
                                        hit={hit} 
                                        key={hit.productId}
                                    />
                                ))}
                            </div>
                            <SubmitButton text="See all search results" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-2 ns-px-4">
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}
